import type { CRMNote, NoteGroupBy } from '../../types/notes';
import { NOTE_TYPE_LABELS } from '../../types/notes';

/** Un groupe de notes affichable (en-tête + liste). */
export interface NoteGroup {
  /** Clé stable pour le rendu/repli (id d'entité, type, ou `__none__`). */
  key: string;
  label: string;
  notes: CRMNote[];
}

/** Clé sentinelle des notes sans valeur sur l'axe courant (ex. sans société). */
const NO_VALUE_KEY = '__none__';

/** Libellé du groupe « sans valeur » par axe. */
const EMPTY_LABELS: Record<Exclude<NoteGroupBy, 'none'>, string> = {
  type: 'Sans type',
  account: 'Sans société',
  contact: 'Sans contact',
  opportunity: 'Sans opportunité',
  meeting: 'Sans RDV',
  lead: 'Sans lead',
};

/** Extrait (clé, libellé) du groupe d'une note selon l'axe choisi. */
function resolveGroup(
  note: CRMNote,
  groupBy: Exclude<NoteGroupBy, 'none'>,
): { key: string; label: string } | null {
  switch (groupBy) {
    case 'type':
      return { key: note.note_type, label: NOTE_TYPE_LABELS[note.note_type] };
    case 'account':
      return note.account ? { key: note.account.id, label: note.account.name } : null;
    case 'contact':
      return note.contact
        ? {
            key: note.contact.id,
            label: `${note.contact.first_name ?? ''} ${note.contact.last_name ?? ''}`.trim() ||
              note.contact.email ||
              'Contact',
          }
        : null;
    case 'opportunity':
      return note.opportunity ? { key: note.opportunity.id, label: note.opportunity.name } : null;
    case 'meeting': {
      // RDV calendaire en priorité, sinon réunion CRM.
      if (note.appointment) {
        return { key: note.appointment.id, label: note.appointment.title || 'RDV' };
      }
      if (note.meeting) {
        return { key: note.meeting.id, label: note.meeting.title || 'Réunion' };
      }
      return null;
    }
    case 'lead':
      return note.lead ? { key: note.lead.id, label: note.lead.company_name } : null;
  }
}

/**
 * Regroupe les notes selon un axe (type, société, contact, opportunité, RDV, lead).
 *
 * - Les notes sans valeur sur l'axe tombent dans un groupe « Sans … » placé en dernier.
 * - Les groupes nommés sont triés par libellé (alpha, insensible à la casse).
 * - À l'intérieur de chaque groupe, les notes épinglées remontent en tête, puis tri
 *   par `created_at` décroissant (cohérent avec l'ordre de la liste plate).
 *
 * Fonction pure : ne dépend que de ses entrées (testable trivialement).
 */
export function groupNotes(notes: CRMNote[], groupBy: NoteGroupBy): NoteGroup[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: '', notes }];
  }

  const groups = new Map<string, NoteGroup>();

  for (const note of notes) {
    const resolved = resolveGroup(note, groupBy);
    const key = resolved?.key ?? NO_VALUE_KEY;
    const label = resolved?.label ?? EMPTY_LABELS[groupBy];

    const existing = groups.get(key);
    if (existing) {
      existing.notes.push(note);
    } else {
      groups.set(key, { key, label, notes: [note] });
    }
  }

  for (const group of groups.values()) {
    group.notes.sort(sortByPinnedThenRecent);
  }

  return [...groups.values()].sort(sortGroups);
}

/** Notes épinglées d'abord, puis les plus récentes. */
function sortByPinnedThenRecent(a: CRMNote, b: CRMNote): number {
  if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
  return b.created_at.localeCompare(a.created_at);
}

/** Groupes nommés triés alpha ; le groupe « Sans … » toujours en dernier. */
function sortGroups(a: NoteGroup, b: NoteGroup): number {
  if (a.key === NO_VALUE_KEY) return 1;
  if (b.key === NO_VALUE_KEY) return -1;
  return a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' });
}
